import type { Dayjs } from 'dayjs';

export const STLY = 'STLY';
export const SDLY = 'SDLY';
export const SWLY = 'SWLY';
export const PP = 'PP';

type ReferenceType = typeof STLY | typeof SDLY | typeof SWLY | typeof PP;

const NUMBER_OF_WEEKS_IN_YEAR = 52;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getPPStart(startDate: Dayjs, endDate: Dayjs) {
  const startWeekday = startDate.isoWeekday();

  const numberOfDaysInRange = endDate.diff(startDate, 'day');
  const previousPeriodStart = startDate.subtract(numberOfDaysInRange, 'day');

  if (startWeekday < previousPeriodStart.isoWeekday()) {
    return previousPeriodStart.isoWeekday(startWeekday);
  }

  return previousPeriodStart.subtract(7, 'day').isoWeekday(startWeekday);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getSDLYEnd(startDate: Dayjs, endDate: Dayjs, sdlyStart: Dayjs) {
  const numberOfDays = endDate.diff(startDate, 'day');
  let sdlyEnd = sdlyStart.add(numberOfDays, 'day');

  // This was added because you were comparing feb 29 with march 1st the year before. This was causing weird results so we decided to remove it
  const isFebInLeapYear = startDate.isLeapYear() && startDate.month() === 1;
  if (isFebInLeapYear) {
    sdlyEnd = sdlyEnd.subtract(1, 'day');
  }

  // SH-13141: Leap year issue in reference period - when comparing to SDLY with reference being a leap year month the leap day is also included
  // Extend the period if either the leap year day is in it, or if we're looking at the whole month of February
  if (sdlyStart.isLeapYear()) {
    const dayBeforeLeapDay = sdlyStart.month(1).date(28);
    const startOfFebLeapYear = sdlyStart.month(1).date(1);

    if (
      sdlyStart.isSameOrBefore(dayBeforeLeapDay) &&
      (sdlyEnd.isAfter(dayBeforeLeapDay) ||
        (sdlyEnd.isSame(dayBeforeLeapDay) &&
          sdlyStart.isSameOrBefore(startOfFebLeapYear)))
    ) {
      sdlyEnd = sdlyEnd.add(1, 'day');
    }
  }

  return sdlyEnd;
}

export function getReferenceStartDate(
  startDate: Dayjs,
  endDate: Dayjs,
  referenceType: ReferenceType,
): Dayjs {
  switch (referenceType) {
    case SDLY:
      return startDate.subtract(1, 'year');
    case STLY:
    case SWLY:
      return startDate.subtract(NUMBER_OF_WEEKS_IN_YEAR, 'week');
    case PP:
      return getPPStart(startDate, endDate);
    default:
      return referenceType satisfies never;
  }
}

export function getReferenceEndDate(
  startDate: Dayjs,
  endDate: Dayjs,
  referenceType: ReferenceType,
): Dayjs {
  const referenceStartDate = getReferenceStartDate(
    startDate,
    endDate,
    referenceType,
  );

  switch (referenceType) {
    case STLY:
    case SWLY:
    case PP: {
      const numberOfDays = endDate.diff(startDate, 'day');
      return referenceStartDate.add(numberOfDays, 'day');
    }
    case SDLY:
      return getSDLYEnd(startDate, endDate, referenceStartDate);
    default:
      return referenceType satisfies never;
  }
}

export function getReferencePeriod(
  startDate: Dayjs,
  endDate: Dayjs,
  referenceType: ReferenceType,
): { end: Dayjs; start: Dayjs } {
  const referenceStartDate = getReferenceStartDate(
    startDate,
    endDate,
    referenceType,
  );
  const referenceEndDate = getReferenceEndDate(
    startDate,
    endDate,
    referenceType,
  );

  return {
    start: referenceStartDate,
    end: referenceEndDate,
  };
}
